import { gql } from '@apollo/client';

import {
  CUSTOMER_FRAGMENT,
  ORDER_DETAIL_FRAGMENT,
  ORDER_SUMMARY_FRAGMENT,
} from 'src/fragments/account';

/**
 * Account operations are centralised rather than colocated with their consumer (the
 * repo's usual habit) because AuthProvider needs Login/RegisterCustomer/GetViewerCustomer
 * and components import AuthProvider — colocating them in a page would create a cycle.
 *
 * NOTE: the operation names here must stay in sync with AUTH_OPERATION_NAMES in
 * src/utils/auth/config.ts. errorLink uses them to avoid treating a failed login as an
 * expired token.
 */

export const LOGIN = gql`
  ${CUSTOMER_FRAGMENT}
  mutation Login($username: String!, $password: String!) {
    login(
      input: {
        provider: PASSWORD
        credentials: { username: $username, password: $password }
      }
    ) {
      authToken
      authTokenExpiration
      refreshToken
      refreshTokenExpiration
      customer {
        ...CustomerFields
        auth {
          wooSessionToken
        }
      }
    }
  }
`;

/**
 * registerCustomer's payload carries no tokens directly, but customer.auth does — so
 * registration is a single round-trip and needs no follow-up login.
 */
export const REGISTER_CUSTOMER = gql`
  ${CUSTOMER_FRAGMENT}
  mutation RegisterCustomer($input: RegisterCustomerInput!) {
    registerCustomer(input: $input) {
      customer {
        ...CustomerFields
        auth {
          authToken
          authTokenExpiration
          refreshToken
          refreshTokenExpiration
          wooSessionToken
        }
      }
    }
  }
`;

/** Takes no id: `customer` resolves from the bearer token, so it cannot leak. */
export const GET_VIEWER_CUSTOMER = gql`
  ${CUSTOMER_FRAGMENT}
  query GetViewerCustomer {
    customer {
      ...CustomerFields
    }
  }
`;

export const SEND_PASSWORD_RESET_EMAIL = gql`
  mutation SendPasswordResetEmail($username: String!) {
    sendPasswordResetEmail(input: { username: $username }) {
      success
    }
  }
`;

export const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword(
    $key: String!
    $login: String!
    $password: String!
  ) {
    resetUserPassword(
      input: { key: $key, login: $login, password: $password }
    ) {
      user {
        databaseId
      }
    }
  }
`;

/**
 * Order history always goes through `customer { orders }`, never the root `orders`
 * query. `customer` takes no id and resolves from the bearer token, so it structurally
 * cannot return another person's orders — the isolation requirement is enforced by the
 * shape of the query rather than by a check we might forget to write.
 */
export const GET_CUSTOMER_ORDERS = gql`
  ${ORDER_SUMMARY_FRAGMENT}
  query GetCustomerOrders($first: Int!, $after: String) {
    customer {
      id
      databaseId
      orders(
        first: $first
        after: $after
        where: { orderby: { field: DATE, order: DESC } }
      ) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          ...OrderSummaryFields
        }
      }
    }
  }
`;

/**
 * Queried by DATABASE_ID, not ORDER_NUMBER: `idType: ORDER_NUMBER` reads `_order_number`
 * post meta, which this store has no plugin to populate, so that lookup always fails.
 * Verified against order 14029. See the plan's Phase 5 note.
 */
export const GET_ORDER = gql`
  ${ORDER_DETAIL_FRAGMENT}
  query GetOrder($databaseId: ID!) {
    order(id: $databaseId, idType: DATABASE_ID) {
      ...OrderDetailFields
    }
  }
`;

export const UPDATE_CUSTOMER = gql`
  ${CUSTOMER_FRAGMENT}
  mutation UpdateCustomer($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) {
      customer {
        ...CustomerFields
      }
    }
  }
`;

/**
 * Orders as the loyalty section needs them.
 *
 * A separate document from GET_CUSTOMER_ORDERS on purpose: that one pages ten at a
 * time and its fragment has no shipping fields, and points are earned on goods
 * excluding shipping. Filtered server-side to the qualifying statuses so a customer
 * with a long history of cancelled orders does not blow the limit on rows worth zero.
 */
export const GET_LOYALTY_ORDERS = gql`
  query GetLoyaltyOrders($first: Int!, $statuses: [OrderStatusEnum]) {
    customer {
      id
      databaseId
      orders(
        first: $first
        where: { statuses: $statuses, orderby: { field: DATE, order: DESC } }
      ) {
        nodes {
          id
          databaseId
          orderNumber
          date
          status
          # FORMATTED (the default). RAW returns null for these on this backend.
          total
          shippingTotal
          shippingTax
        }
      }
    }
  }
`;

/**
 * The authoritative balance.
 *
 * It lives on `User`, not on WooGraphQL's `Customer`, because it is an ACF field on
 * the user — hence `viewer` rather than `customer`, and hence the doubled name: the
 * outer `loyaltyPoints` is the ACF *group* (wp-graphql-acf names the group type after
 * the field group), the inner one is the Float field inside it. It is null for anyone
 * the admin has not seeded, so normaliseBalance() guards every read.
 *
 * Only issued when LOYALTY.USE_SERVER_BALANCE is true.
 */
export const GET_LOYALTY_BALANCE = gql`
  query GetLoyaltyBalance {
    viewer {
      id
      databaseId
      loyaltyPoints {
        loyaltyPoints
      }
    }
  }
`;

/**
 * The products behind the configured rewards. `include` takes database IDs and ignores
 * ones that no longer exist, so a deleted reward simply comes back missing rather than
 * erroring — rewardLadder() then marks it unavailable.
 */
export const GET_REWARD_PRODUCTS = gql`
  query GetRewardProducts($ids: [Int]) {
    products(where: { include: $ids }, first: 100) {
      nodes {
        id
        databaseId
        name
        uri
        ... on SimpleProduct {
          price
          stockStatus
          image {
            sourceUrl(size: WOOCOMMERCE_THUMBNAIL)
            altText
          }
        }
      }
    }
  }
`;

/**
 * The wishlist's saved products. Same `include`-ignores-missing-ids shape as
 * GET_REWARD_PRODUCTS — a deleted or unpublished product simply comes back
 * missing rather than erroring, so it silently drops out of the list.
 */
export const GET_WISHLIST_PRODUCTS = gql`
  query GetWishlistProducts($ids: [Int]) {
    products(where: { include: $ids }, first: 100) {
      nodes {
        id
        databaseId
        name
        uri
        ... on SimpleProduct {
          price
          regularPrice
          onSale
          stockStatus
          image {
            sourceUrl(size: WOOCOMMERCE_THUMBNAIL)
            altText
          }
          productFields {
            brand
          }
        }
      }
    }
  }
`;
